'use client';

import { Download01Icon, Delete02Icon } from '@terra/ui-icons';
import { TechnologyIcon } from '../technology-icon';
import type { InternalEnrichedFinding } from '../../types';
import {
  POC_SCRIPT_ACTION_KEYS,
  PocActionsMode,
  type PocScriptActionKey,
} from './poc-actions-mode';

function PocScriptIcon({ size = 14 }: { size?: number }) {
  return <TechnologyIcon name="python" size={size} />;
}

type PocScriptActionsParams = {
  finding: Pick<
    InternalEnrichedFinding,
    'pocScript' | 'pocScriptBlockedByGuardrails'
  >;
  t: (key: string) => string;
  onGeneratePocScript?: () => void;
  onRegeneratePocScript?: () => void;
  onDownloadPocScript?: () => void;
  onDeletePocScript?: () => void;
  isGeneratingPocScript: boolean;
  isDeletingPocScript?: boolean;
  pocActionsMode?: PocActionsMode;
  downloadButtonLabelOverride?: string;
  downloadButtonTooltipOverride?: string;
  mutationsDisabled?: boolean;
  mutationsDisabledTooltip?: string;
};

export function getPocScriptActions({
  finding,
  t,
  onGeneratePocScript,
  onRegeneratePocScript,
  onDownloadPocScript,
  onDeletePocScript,
  isGeneratingPocScript,
  isDeletingPocScript = false,
  pocActionsMode = PocActionsMode.Full,
  downloadButtonLabelOverride,
  downloadButtonTooltipOverride,
  mutationsDisabled,
  mutationsDisabledTooltip,
}: PocScriptActionsParams): Array<{
  label: string;
  onClick: () => void;
  variant: 'ghost';
  icon: typeof PocScriptIcon | typeof Download01Icon | typeof Delete02Icon;
  loading?: boolean;
  disabled?: boolean;
  colorScheme?: string;
  tooltip?: string;
  actionType?: PocScriptActionKey;
}> {
  const effectiveMode =
    pocActionsMode === PocActionsMode.Dropdown
      ? PocActionsMode.Full
      : pocActionsMode;
  const actions: Array<{
    label: string;
    onClick: () => void;
    variant: 'ghost';
    icon: typeof PocScriptIcon | typeof Download01Icon | typeof Delete02Icon;
    loading?: boolean;
    disabled?: boolean;
    colorScheme?: string;
    tooltip?: string;
    actionType?: PocScriptActionKey;
  }> = [];

  if (effectiveMode === PocActionsMode.DownloadOnly) {
    if (onDownloadPocScript && finding.pocScript) {
      actions.push({
        label:
          downloadButtonLabelOverride ?? t(POC_SCRIPT_ACTION_KEYS.Download),
        onClick: onDownloadPocScript,
        variant: 'ghost',
        icon: Download01Icon,
        actionType: POC_SCRIPT_ACTION_KEYS.Download,
        ...(downloadButtonTooltipOverride && {
          tooltip: downloadButtonTooltipOverride,
        }),
      });
    }

    return withPocMutationDisable({
      actions,
      mutationsDisabled,
      mutationsDisabledTooltip,
    });
  }

  if (finding.pocScriptBlockedByGuardrails) {
    if (onRegeneratePocScript) {
      actions.push({
        label: isGeneratingPocScript
          ? t('generatingPocScript')
          : t(POC_SCRIPT_ACTION_KEYS.Regenerate),
        onClick: onRegeneratePocScript,
        variant: 'ghost',
        icon: PocScriptIcon,
        loading: isGeneratingPocScript,
        disabled: isGeneratingPocScript,
        actionType: POC_SCRIPT_ACTION_KEYS.Regenerate,
      });
    }

    if (onDeletePocScript) {
      actions.push({
        label: t(POC_SCRIPT_ACTION_KEYS.Delete),
        onClick: onDeletePocScript,
        variant: 'ghost',
        icon: Delete02Icon,
        loading: isDeletingPocScript,
        disabled: isDeletingPocScript,
        colorScheme: 'red',
        actionType: POC_SCRIPT_ACTION_KEYS.Delete,
      });
    }

    return withPocMutationDisable({
      actions,
      mutationsDisabled,
      mutationsDisabledTooltip,
    });
  }

  if (onGeneratePocScript && !finding.pocScript) {
    actions.push({
      label: isGeneratingPocScript
        ? t('generatingPocScript')
        : t(POC_SCRIPT_ACTION_KEYS.Generate),
      onClick: onGeneratePocScript,
      variant: 'ghost',
      icon: PocScriptIcon,
      loading: isGeneratingPocScript,
      disabled: isGeneratingPocScript,
      actionType: POC_SCRIPT_ACTION_KEYS.Generate,
    });
  }

  if (onRegeneratePocScript && finding.pocScript) {
    actions.push({
      label: isGeneratingPocScript
        ? t('generatingPocScript')
        : t(POC_SCRIPT_ACTION_KEYS.Regenerate),
      onClick: onRegeneratePocScript,
      variant: 'ghost',
      icon: PocScriptIcon,
      loading: isGeneratingPocScript,
      disabled: isGeneratingPocScript,
      actionType: POC_SCRIPT_ACTION_KEYS.Regenerate,
    });
  }

  if (onDownloadPocScript && finding.pocScript) {
    actions.push({
      label: downloadButtonLabelOverride ?? t(POC_SCRIPT_ACTION_KEYS.Download),
      onClick: onDownloadPocScript,
      variant: 'ghost',
      icon: Download01Icon,
      actionType: POC_SCRIPT_ACTION_KEYS.Download,
      ...(downloadButtonTooltipOverride && {
        tooltip: downloadButtonTooltipOverride,
      }),
    });
  }

  if (onDeletePocScript && finding.pocScript) {
    actions.push({
      label: t(POC_SCRIPT_ACTION_KEYS.Delete),
      onClick: onDeletePocScript,
      variant: 'ghost',
      icon: Delete02Icon,
      loading: isDeletingPocScript,
      disabled: isDeletingPocScript,
      colorScheme: 'red',
      actionType: POC_SCRIPT_ACTION_KEYS.Delete,
    });
  }

  return withPocMutationDisable({
    actions,
    mutationsDisabled,
    mutationsDisabledTooltip,
  });
}

function withPocMutationDisable({
  actions,
  mutationsDisabled,
  mutationsDisabledTooltip,
}: {
  actions: Array<{
    label: string;
    onClick: () => void;
    variant: 'ghost';
    icon: typeof PocScriptIcon | typeof Download01Icon | typeof Delete02Icon;
    loading?: boolean;
    disabled?: boolean;
    colorScheme?: string;
    tooltip?: string;
    actionType?: PocScriptActionKey;
  }>;
  mutationsDisabled?: boolean;
  mutationsDisabledTooltip?: string;
}) {
  if (!mutationsDisabled) {
    return actions;
  }

  return actions.map((action) =>
    action.actionType === POC_SCRIPT_ACTION_KEYS.Download
      ? action
      : {
          ...action,
          disabled: true,
          tooltip: mutationsDisabledTooltip ?? action.tooltip,
        },
  );
}
